import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  KeyRound,
  LockKeyhole,
  Mail,
  Smartphone,
} from "lucide-react";
import ekeyLogo from "../../assets/images/eKey.webp";
import backArrow from "../../assets/images/greenbackarrow.svg";
import { Card } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import {
  fetchProfileInfo,
  selectProfileData,
  selectProfileDataLoader,
} from "../../redux/features/profileSlice";

/**
 * Profile (mobile, from More).
 *
 * Read-only details presented as a profile, not as a form: the old page drew
 * every field as a disabled input (which reads as broken) and pinned a Save
 * button that only raised an alert, since nothing here is editable. What can
 * be changed has its own flow, reached from the row it concerns.
 *
 * Desktop settings keep their own ProfileDetails component; this page does
 * not touch it.
 */

// No entrance of its own: this is a page people open often to check a
// detail, and the app already fades every page in on navigation. A section
// stagger on top only made the details arrive last.

const SectionTitle = ({ children }) => (
  <h2 className="mb-2 px-1 text-[#5B6871] text-sm font-medium leading-5">
    {children}
  </h2>
);

// One line of a details card: icon tile, label over value, optional action.
// Values wrap rather than truncate: a phone number or email cut to "…" is
// exactly the detail someone opened this page to check.
const DetailRow = ({ icon: Icon, label, value, action, loading, dir }) => (
  <div className="flex items-center gap-3 px-4 py-3.5 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-[#F0F2F3]">
    <span
      aria-hidden="true"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F1F4F6]"
    >
      <Icon className="h-[18px] w-[18px] text-[#48535B]" />
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-[#6C7A83] text-[13px] leading-[18px]">{label}</p>
      {loading ? (
        <Skeleton className="mt-1.5 h-4 w-40" />
      ) : (
        <p
          dir={dir}
          className="text-[#1A2024] text-base font-medium leading-6 [overflow-wrap:anywhere] rtl:text-right"
        >
          {value || "—"}
        </p>
      )}
    </div>
    {action}
  </div>
);

const RowAction = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex shrink-0 items-center gap-0.5 rounded-lg px-2 py-1.5 text-[#0A7CAE] text-sm font-semibold leading-5 transition-[background-color,transform] duration-150 ease-out hover:bg-[#F0FAFF] active:scale-[0.97]"
  >
    {children}
    <ChevronRight aria-hidden="true" className="h-4 w-4 rtl:rotate-180" />
  </button>
);

const MobileProfilePage = () => {
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profile = useSelector(selectProfileData);
  const isFetching = useSelector(selectProfileDataLoader);
  const loading = isFetching && !profile;

  let isEkey = false;
  try {
    isEkey = localStorage.getItem("ekeyLogin") === "true";
  } catch (e) {
    /* storage unavailable: treat as a password account */
  }

  useEffect(() => {
    dispatch(fetchProfileInfo());
  }, [dispatch]);

  const firstName = profile?.FirstName || "";
  const lastName = profile?.LastName || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return (
    <div className="mx-auto w-[500px] max-w-full px-5 pt-[19px] pb-12">
      <button
        type="button"
        aria-label={translate("Back", "رجوع")}
        onClick={() => navigate("/more")}
        className="-ms-1 flex h-10 w-10 items-center justify-center rounded-full transition-transform duration-150 ease-out active:scale-[0.94]"
      >
        <img src={backArrow} alt="" className="h-10 w-10 rtl:rotate-180" />
      </button>

      <div className="mt-5 mb-6">
        <h1 className="text-[#1A2024] text-2xl font-semibold leading-8 tracking-[-0.24px]">
          {translate("Your profile", "ملفك الشخصي")}
        </h1>
        <p className="mt-1 text-[#5B6871] text-base leading-6">
          {translate(
            "Your details as they appear on your account.",
            "بياناتك كما هي مسجلة في حسابك."
          )}
        </p>
      </div>

      {/* Identity: name and CPR. No avatar: the portal holds no profile
          photos, and an initials disc was decoration standing in for one. */}
      <Card
        className="mb-6 rounded-[14px] border-[#E5E9EB] px-4 py-3.5 shadow-none"
      >
        {loading ? (
          <div className="space-y-2 py-0.5">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-4 w-32" />
          </div>
        ) : (
          <>
            <div className="min-w-0">
              <p className="truncate text-[#1A2024] text-lg font-semibold leading-7">
                {fullName || "—"}
              </p>
              <p className="text-[#5B6871] text-sm leading-5">
                {t("profileSettings.cpr_cr_number")}{" "}
                <span dir="ltr" className="tabular-nums text-[#48535B] font-medium">
                  {profile?.CPR || "—"}
                </span>
              </p>
            </div>
          </>
        )}
      </Card>

      {/* Contact */}
      <section className="mb-6">
        <SectionTitle>
          {translate("Contact details", "بيانات التواصل")}
        </SectionTitle>
        <Card className="overflow-hidden rounded-[14px] border-[#E5E9EB] shadow-none">
          <DetailRow
            icon={Smartphone}
            label={t("profileSettings.mobile_number")}
            value={profile?.PhoneNumber}
            dir="ltr"
            loading={loading}
            action={
              !isEkey && (
                <RowAction onClick={() => navigate("/change-mobile-number")}>
                  {translate("Change", "تغيير")}
                </RowAction>
              )
            }
          />
          <DetailRow
            icon={Mail}
            label={t("profileSettings.email_address")}
            value={profile?.Email}
            dir="ltr"
            loading={loading}
          />
        </Card>
      </section>

      {/* Sign-in */}
      <section>
        <SectionTitle>
          {translate("Sign-in & security", "تسجيل الدخول والأمان")}
        </SectionTitle>
        <Card className="overflow-hidden rounded-[14px] border-[#E5E9EB] shadow-none">
          {isEkey ? (
            <div className="flex items-center gap-3 px-4 py-3.5">
              <span
                aria-hidden="true"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F1F4F6]"
              >
                <KeyRound className="h-[18px] w-[18px] text-[#48535B]" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[#6C7A83] text-[13px] leading-[18px]">
                  {t("profileSettings.auth_method")}
                </p>
                <p className="text-[#1A2024] text-sm leading-5">
                  {t("profileSettings.auth_desc")}
                </p>
              </div>
              <img src={ekeyLogo} alt="eKey 2.0" className="h-7 w-auto shrink-0" />
            </div>
          ) : (
            <DetailRow
              icon={LockKeyhole}
              label={translate("Password", "كلمة المرور")}
              value="••••••••••••"
              dir="ltr"
              action={
                <RowAction onClick={() => navigate("/change-password")}>
                  {translate("Change", "تغيير")}
                </RowAction>
              }
            />
          )}
        </Card>
      </section>
    </div>
  );
};

export default MobileProfilePage;
